import * as React from "react";
import { Select, Button, Divider } from "antd";
import dayjs from "dayjs";
import { FormLabel } from "../../components/antd/form/FormLabel";
import { InfoTooltip } from "../../components/InfoTooltip";
import { useAppSelector } from "../../store/hooks";
import { selectLabel } from "../../store/timezoneSlice";
import { useMetricsLabelValues } from "./useMetricsLabelValues";

export interface MetricsFiltersState {
  gvc: string[];
  workload: string[];
  location: string[];
  container: string[];
  percentile: number;
  grouping: string[];
  timeRange: string;
  fromIso: string | null;
  toIso: string | null;
}

interface MetricsFiltersProps {
  value: MetricsFiltersState;
  onChange: (value: MetricsFiltersState) => void;
  lockedGvc?: string[];
  lockedWorkload?: string[];
}

const PERCENTILE_OPTIONS = [
  { label: "50th", value: 50 },
  { label: "75th", value: 75 },
  { label: "90th", value: 90 },
  { label: "95th", value: 95 },
  { label: "99th", value: 99 },
];

const ALL_GROUPING_OPTIONS = [
  { label: "Location", value: "location" },
  { label: "GVC", value: "gvc" },
  { label: "Item", value: "item" },
  { label: "Version", value: "version" },
  { label: "Replica", value: "replica" },
  { label: "Container Name", value: "container_name" },
];

const TIME_RANGE_OPTIONS = [
  { label: "Last 15 minutes", value: "15m" },
  { label: "Last 1 hour", value: "1h" },
  { label: "Last 6 hours", value: "6h" },
  { label: "Last 1 day", value: "1d" },
  { label: "Last 7 days", value: "7d" },
  { label: "Last 30 days", value: "30d" },
];

const getTimeRangeValues = (timeRange: string): { fromIso: string; toIso: null } => {
  const match = timeRange.match(/^(\d+)([mhd])$/);
  if (!match) {
    return { fromIso: dayjs().subtract(1, "hour").toISOString(), toIso: null };
  }
  const [, amount, unit] = match;
  const unitMap: Record<string, "minute" | "hour" | "day"> = {
    m: "minute",
    h: "hour",
    d: "day",
  };
  return {
    fromIso: dayjs().subtract(Number(amount), unitMap[unit]).toISOString(),
    toIso: null,
  };
};

const percentileStyle = { width: 100 };
const timeRangeStyle = { width: 160 };

export const getDefaultFiltersState = (): MetricsFiltersState => ({
  gvc: [],
  workload: [],
  location: [],
  container: [],
  percentile: 90,
  grouping: [],
  timeRange: "1h",
  fromIso: dayjs().subtract(1, "hour").toISOString(),
  toIso: null,
});

export const MetricsFilters: React.FC<MetricsFiltersProps> = ({ value, onChange, lockedGvc, lockedWorkload }) => {
  const isGvcLocked = lockedGvc !== undefined && lockedGvc.length > 0;
  const isWorkloadLocked = lockedWorkload !== undefined && lockedWorkload.length > 0;
  const { gvcs, workloads, locations, containers, isLoading: labelValuesLoading } = useMetricsLabelValues();

  const timezoneLabel = useAppSelector(selectLabel);

  const [focusedField, setFocusedField] = React.useState<string | null>(null);
  const [isSticky, setIsSticky] = React.useState(false);
  const sentinelRef = React.useRef<HTMLDivElement>(null);

  // Track when the filters become sticky using Intersection Observer
  React.useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // When sentinel is not visible, the filters are sticky
        setIsSticky(!entry.isIntersecting);
      },
      { threshold: 0 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const updateField = <K extends keyof MetricsFiltersState>(field: K, fieldValue: MetricsFiltersState[K]) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const getSelectStyle = (field: string, selectedCount: number): React.CSSProperties => {
    const isFocused = focusedField === field;
    if (isFocused) {
      // When focused: grow by width and height as needed
      return {
        minWidth: 200,
        width: "auto",
      };
    }
    // When not focused: limit width to 800px max, single row height
    return {
      minWidth: 200,
      maxWidth: 800,
      width: "auto",
      maxHeight: selectedCount >= 5 ? 32 : undefined,
      overflow: selectedCount >= 5 ? "hidden" : undefined,
    };
  };

  const getMaxTagCount = (field: string, selectedCount: number): number | undefined => {
    const isFocused = focusedField === field;
    // When focused: show all items (no limit)
    if (isFocused) return undefined;
    // When not focused and >= 5 items: show "{count} Selected"
    if (selectedCount >= 5) return 0;
    // When not focused and < 5 items: show all names
    return undefined;
  };

  const maxTagPlaceholder = (omittedValues: any[]) => `${omittedValues.length} Selected`;

  const gvcOptions = React.useMemo(() => {
    return gvcs.map((name) => ({ label: name, value: name }));
  }, [gvcs]);

  const workloadOptions = React.useMemo(() => {
    return workloads.map((name) => ({ label: name, value: name }));
  }, [workloads]);

  const locationOptions = React.useMemo(() => {
    return locations.map((name) => ({ label: name, value: name }));
  }, [locations]);

  const containerOptions = React.useMemo(() => {
    return containers.map((name) => ({ label: name, value: name }));
  }, [containers]);

  // Filter out locked values from grouping options and rename "Item" to "Workload" when GVC is locked
  const groupingOptions = React.useMemo(() => {
    return ALL_GROUPING_OPTIONS.filter((option) => {
      if (isGvcLocked && option.value === "gvc") return false;
      if (isWorkloadLocked && option.value === "item") return false;
      return true;
    }).map((option) => {
      if (isGvcLocked && option.value === "item") {
        return { ...option, label: "Workload" };
      }
      return option;
    });
  }, [isGvcLocked, isWorkloadLocked]);

  const addTestOptions = () => {
    const gvcValues = gvcOptions.slice(0, Math.min(5, gvcOptions.length)).map((o) => o.value);
    const workloadValues = workloadOptions.slice(0, Math.min(4, workloadOptions.length)).map((o) => o.value);
    const locationValues = locationOptions.slice(0, Math.min(3, locationOptions.length)).map((o) => o.value);
    const containerValues = containerOptions.slice(0, Math.min(3, containerOptions.length)).map((o) => o.value);

    onChange({
      ...value,
      gvc: gvcValues,
      workload: workloadValues,
      location: locationValues,
      container: containerValues,
      grouping: ["location", "gvc", "workload", "version"],
    });
  };

  const clearTestOptions = () => {
    onChange(getDefaultFiltersState());
  };

  const renderDropdown = (
    field: keyof MetricsFiltersState,
    options: { label: string; value: string }[],
    menu: React.ReactElement,
  ) => (
    <>
      <div className="flex gap-2 p-2">
        <Button size="small" type="default" onClick={() => updateField(field, options.map((o) => o.value) as any)}>
          All
        </Button>
        <Button size="small" type="default" onClick={() => updateField(field, [] as any)}>
          None
        </Button>
      </div>
      <Divider style={{ margin: 0 }} />
      {menu}
    </>
  );

  return (
    <>
      {/* Sentinel element to detect when filters become sticky */}
      <div ref={sentinelRef} style={{ height: 1, marginBottom: -1 }} />
      <div
        className={`flex flex-wrap gap-4 mb-4 p-4 sticky top-0 z-10 ${isSticky ? "shadow-md" : ""}`}
        style={{
          backgroundColor: "var(--color-drop)",
          border: "1px solid var(--color-border)",
          borderRadius: isSticky ? "0 0 8px 8px" : "8px",
        }}
      >
        {!isGvcLocked && (
          <div className="flex flex-col">
            <FormLabel>GVC</FormLabel>
            <Select
              mode="multiple"
              style={getSelectStyle("gvc", value.gvc.length)}
              value={value.gvc}
              onChange={(val) => updateField("gvc", val)}
              options={gvcOptions}
              placeholder="All"
              loading={labelValuesLoading}
              onFocus={() => setFocusedField("gvc")}
              onBlur={() => setFocusedField(null)}
              maxTagCount={getMaxTagCount("gvc", value.gvc.length)}
              maxTagPlaceholder={maxTagPlaceholder}
              dropdownRender={(menu) => renderDropdown("gvc", gvcOptions, menu)}
              filterOption={(input, option) =>
                ((option?.label || "") as string).toLowerCase().includes(input.toLowerCase())
              }
            />
          </div>
        )}

        {!isWorkloadLocked && (
          <div className="flex flex-col">
            <FormLabel>Workload</FormLabel>
            <Select
              mode="multiple"
              style={getSelectStyle("workload", value.workload.length)}
              value={value.workload}
              onChange={(val) => updateField("workload", val)}
              options={workloadOptions}
              placeholder="All"
              loading={labelValuesLoading}
              onFocus={() => setFocusedField("workload")}
              onBlur={() => setFocusedField(null)}
              maxTagCount={getMaxTagCount("workload", value.workload.length)}
              maxTagPlaceholder={maxTagPlaceholder}
              dropdownRender={(menu) => renderDropdown("workload", workloadOptions, menu)}
              filterOption={(input, option) =>
                ((option?.label || "") as string).toLowerCase().includes(input.toLowerCase())
              }
            />
          </div>
        )}

        <div className="flex flex-col">
          <FormLabel>Location</FormLabel>
          <Select
            mode="multiple"
            style={getSelectStyle("location", value.location.length)}
            value={value.location}
            onChange={(val) => updateField("location", val)}
            options={locationOptions}
            placeholder="All"
            loading={labelValuesLoading}
            onFocus={() => setFocusedField("location")}
            onBlur={() => setFocusedField(null)}
            maxTagCount={getMaxTagCount("location", value.location.length)}
            maxTagPlaceholder={maxTagPlaceholder}
            dropdownRender={(menu) => renderDropdown("location", locationOptions, menu)}
            filterOption={(input, option) =>
              ((option?.label || "") as string).toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>

        <div className="flex flex-col">
          <FormLabel>Container</FormLabel>
          <Select
            mode="multiple"
            style={getSelectStyle("container", value.container.length)}
            value={value.container}
            onChange={(val) => updateField("container", val)}
            options={containerOptions}
            placeholder="All"
            loading={labelValuesLoading}
            onFocus={() => setFocusedField("container")}
            onBlur={() => setFocusedField(null)}
            maxTagCount={getMaxTagCount("container", value.container.length)}
            maxTagPlaceholder={maxTagPlaceholder}
            dropdownRender={(menu) => renderDropdown("container", containerOptions, menu)}
            filterOption={(input, option) =>
              ((option?.label || "") as string).toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>

        <div className="flex flex-col">
          <FormLabel>Percentile</FormLabel>
          <Select
            style={percentileStyle}
            value={value.percentile}
            onChange={(val) => updateField("percentile", val)}
            options={PERCENTILE_OPTIONS}
          />
        </div>

        <div className="flex flex-col">
          <div className="flex items-center">
            <FormLabel>Group By</FormLabel>
            {isGvcLocked || isWorkloadLocked ? null : (
              <InfoTooltip
                title={
                  <div>
                    <p>
                      Item option groups by context of the chart, for workload related charts, it is workload, for load
                      balancer related chart, it is GVC etc.
                    </p>
                  </div>
                }
                style={{ marginLeft: 4 }}
              />
            )}
          </div>
          <Select
            mode="multiple"
            style={getSelectStyle("grouping", value.grouping.length)}
            value={value.grouping.filter((g) => {
              // Don't show locked values in the selection
              if (isGvcLocked && g === "gvc") return false;
              if (isWorkloadLocked && g === "item") return false;
              return true;
            })}
            onChange={(val) => updateField("grouping", val)}
            options={groupingOptions}
            placeholder="Select grouping"
            onFocus={() => setFocusedField("grouping")}
            onBlur={() => setFocusedField(null)}
            maxTagCount={getMaxTagCount("grouping", value.grouping.length)}
            maxTagPlaceholder={maxTagPlaceholder}
            dropdownRender={(menu) => renderDropdown("grouping", groupingOptions, menu)}
          />
        </div>

        <div className="flex flex-col">
          <FormLabel>Time Range ({timezoneLabel})</FormLabel>
          <Select
            style={timeRangeStyle}
            value={value.timeRange}
            onChange={(val) => {
              const { fromIso, toIso } = getTimeRangeValues(val);
              onChange({ ...value, timeRange: val, fromIso, toIso });
            }}
            options={TIME_RANGE_OPTIONS}
          />
        </div>
      </div>
    </>
  );
};
